/**
 * MillionMileTicker — slim top-of-page ticker showing the Million Mile
 * Movement umbrella counter. Designed to live just under the rolling
 * banner / above the sticky nav. Polls the public global counter every
 * 45 s with an animated count-up + pulsing live dot, links to /stepathon.
 *
 * Includes a one-tap Share button: uses the native Web Share API on
 * mobile, falls back to clipboard on desktop. Every share is a free
 * top-of-funnel impression for the campaign.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import API from "@/lib/api";
import useHideOnModal from "../hooks/useHideOnModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 45_000;

function useCountUp(target, ms = 1200) {
  const [val, setVal] = useState(0);
  const startRef = useRef(0);
  useEffect(() => {
    const from = startRef.current;
    if (from === target) return;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
      else startRef.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return val;
}

export default function MillionMileTicker() {
  const hiddenByModal = useHideOnModal();
  const [enabled, setEnabled] = useState(true);
  const [d, setD] = useState({ total_steps: 0, total_miles: 0,
                               participant_count: 0 });

  useEffect(() => {
    let dead = false;
    // One-shot: check admin toggle so the strip disappears when disabled.
    API.get("/public/strip-toggles")
      .then((r) => { if (!dead) setEnabled(r.data?.million_mile_enabled !== false); })
      .catch(() => {});
    const fetchOnce = () => {
      axios
        .get(`${BACKEND}/api/stepathon/global/counter`)
        .then((r) => { if (!dead) setD(r.data); })
        .catch(() => {});
    };
    fetchOnce();
    const id = setInterval(fetchOnce, REFRESH_MS);
    return () => { dead = true; clearInterval(id); };
  }, []);

  const steps = useCountUp(d.total_steps);
  const miles = useCountUp(Math.round(d.total_miles));

  if (hiddenByModal || !enabled) return null;

  const onShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = typeof window !== "undefined"
      ? `${window.location.origin}/stepathon`
      : "https://glpflo.org/stepathon";
    const text =
      `Help GLPFlo hit 100 million steps — `
      + `${d.total_steps.toLocaleString()} steps walked so far across the `
      + `Million Mile Movement. Join the Step-a-Thon: ${url}`;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "GLPFlo · Million Mile Movement",
                                text, url });
        return;
      } catch { /* user cancelled — fall through to clipboard */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Share link copied — paste anywhere!");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  return (
    <div className="relative z-[55] bg-gradient-to-r from-[#1B0B3A]
                    via-[#3B0764] to-[#1B0B3A] text-white"
         data-testid="million-mile-ticker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center
                      justify-center gap-3 text-xs sm:text-sm flex-wrap">
        <Link to="/stepathon"
              className="inline-flex items-center gap-3 hover:brightness-110 transition">
          <span className="inline-flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full
                               rounded-full bg-pink-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-400" />
            </span>
            <span className="font-bold tracking-[0.25em] uppercase text-pink-200
                             text-[10px] sm:text-xs">
              Million Mile Movement
            </span>
          </span>
          <span className="hidden sm:inline text-purple-200/50">·</span>
          <span className="tabular-nums font-extrabold text-white" translate="no">
            {steps.toLocaleString()}
          </span>
          <span className="text-purple-200/85">steps</span>
          <span className="hidden sm:inline text-purple-200/50">·</span>
          <span className="hidden sm:inline tabular-nums text-pink-200" translate="no">
            {miles.toLocaleString()}<span className="ml-1">miles</span>
          </span>
          <span className="hidden md:inline text-purple-200/50">·</span>
          <span className="hidden md:inline text-purple-100">
            <span translate="no">{(d.participant_count || 0).toLocaleString()}</span> walkers
          </span>
          <span className="ml-2 px-2.5 py-0.5 rounded-full bg-pink-500/20
                           text-pink-200 font-bold text-[10px] sm:text-xs">
            Join →
          </span>
        </Link>
        <button
          type="button"
          onClick={onShare}
          data-testid="million-mile-share-btn"
          aria-label="Share Million Mile Movement"
          className="ml-1 px-2.5 py-0.5 rounded-full bg-white/10 hover:bg-white/20
                     transition text-pink-100 hover:text-white font-semibold
                     text-[10px] sm:text-xs inline-flex items-center gap-1"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round"
               strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          Share
        </button>
      </div>
    </div>
  );
}
